import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { AddItemModal } from "./AddItemModal";
import { formatDateTime } from "../../utils/formatDate";
import type { PersonalInventoryEntry } from "../../types";

/** Eigener Bestand: Tabelle der eigenen Gegenstände mit Mengen-Schnellanpassung, Bearbeiten und Entfernen. */
export function MyInventoryTab() {
  const canAdd = usePermission("personal_inventory.add_items");
  const canEdit = usePermission("personal_inventory.edit");
  const canAdjust = usePermission("personal_inventory.adjust_quantity");

  const [entries, setEntries] = useState<PersonalInventoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get<PersonalInventoryEntry[]>("/api/personal-inventory/mine")
      .then(setEntries)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function adjust(itemId: number, delta: number) {
    setError(null);
    setBusyId(itemId);
    try {
      await api.post(`/api/personal-inventory/mine/${itemId}/adjust`, { delta });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Änderung fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  function startEdit(entry: PersonalInventoryEntry) {
    setEditingId(entry.item_id);
    setEditValue(entry.quantity);
  }

  async function saveEdit(itemId: number) {
    setError(null);
    setBusyId(itemId);
    try {
      await api.patch(`/api/personal-inventory/mine/${itemId}`, { quantity: editValue });
      setEditingId(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(entry: PersonalInventoryEntry) {
    if (!confirm(`"${entry.item_name}" wirklich aus dem eigenen Bestand entfernen?`)) return;
    setBusyId(entry.item_id);
    try {
      await api.delete(`/api/personal-inventory/mine/${entry.item_id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Entfernen fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{entries.length} Gegenstand/Gegenstände im eigenen Bestand.</p>
        {canAdd && <button className="btn-primary" onClick={() => setAddOpen(true)}>+ Gegenstand hinzufügen</button>}
      </div>

      {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

      <Card padded={false} className="overflow-x-auto">
        {loading ? <Spinner /> : entries.length === 0 ? (
          <EmptyState message="Noch keine Gegenstände im eigenen Bestand." />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>Gegenstand</th>
                <th>Kategorie</th>
                <th>Menge</th>
                <th>Letzte Änderung</th>
                <th>Geändert von</th>
                {(canEdit || canAdjust) && <th />}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="font-medium text-gray-100">{entry.item_name}</td>
                  <td className="text-gray-400">{entry.category_name ?? "—"}</td>
                  <td>
                    {editingId === entry.item_id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number" min={0} value={editValue}
                          onChange={(e) => setEditValue(Number(e.target.value))}
                          className="input w-20 py-1"
                        />
                        <button className="text-accent text-xs hover:underline" disabled={busyId === entry.item_id} onClick={() => saveEdit(entry.item_id)}>Speichern</button>
                        <button className="text-gray-500 text-xs hover:underline" onClick={() => setEditingId(null)}>Abbrechen</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {canAdjust && (
                          <button
                            className="w-6 h-6 rounded bg-surface-raised border border-surface-border text-gray-300 hover:bg-surface-border disabled:opacity-40"
                            disabled={busyId === entry.item_id || entry.quantity <= 0}
                            onClick={() => adjust(entry.item_id, -1)}
                          >−</button>
                        )}
                        <span className="text-gray-200 font-medium w-16 text-center">{entry.quantity} {entry.unit}</span>
                        {canAdjust && (
                          <button
                            className="w-6 h-6 rounded bg-surface-raised border border-surface-border text-gray-300 hover:bg-surface-border disabled:opacity-40"
                            disabled={busyId === entry.item_id}
                            onClick={() => adjust(entry.item_id, 1)}
                          >+</button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="text-gray-500 whitespace-nowrap text-xs">{formatDateTime(entry.updated_at)}</td>
                  <td className="text-gray-500 text-xs">{entry.updated_by_name ?? "—"}</td>
                  {(canEdit || canAdjust) && (
                    <td className="text-right whitespace-nowrap">
                      {canEdit && editingId !== entry.item_id && (
                        <>
                          <button className="text-accent text-xs hover:underline mr-3" onClick={() => startEdit(entry)}>Bearbeiten</button>
                          <button className="text-danger text-xs hover:underline" onClick={() => remove(entry)}>Entfernen</button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {addOpen && <AddItemModal existing={entries} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}
